/**
 * Fatture › Entrate fisse (26/10/2026).
 *
 * La domanda: quanto ci entra ogni mese dai canoni che fatturiamo a mano, e da chi?
 * La forma: come le spese fisse, ma al contrario: una riga per canone col logo o le iniziali del
 * cliente, quanto e ogni quanto, e il progetto che il canone paga (con il link) quando c'è.
 *
 * Perché qui: i "canoni a mano" (l'affitto di AMA, i servizi di più mesi come il digital
 * marketing: modello RecurringRevenue, GET /admin/recurring-revenue) NON sono abbonamenti.
 * Decisione di Lorenzo del 26/10/2026: tolti dalla pagina Abbonamenti (lì c'era la scheda
 * "Fuori Stripe", features/services/OffStripeTab.tsx), hanno casa in Fatture.
 * Si mostrano quelli in corso e quelli che devono partire; finiti e fermi a richiesta.
 * Gli importi sono quelli che arrivano in banca, IVA compresa (come il totale dell'API).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Icon } from '../../../components/ui/Icon';
import { BrandLogo } from '../../../components/ui/BrandLogo';
import { MEmpty, MGroup, MRow, iniziali } from '../../../components/mobile/MobileKit';
import { fmtEur } from '../../../lib/utils';
import { euroRiga, nomeRiga } from '../shared/AdminMobile';
import { OGNI_QUANTO_ENTRATA, entrateFisseQuery, faseEntrata, giornoInParole, ilGiorno, alGiorno, dalGiorno, delGiorno, nomeBreve, type EntrataFissa } from './fattureApi';

/**
 * Chi paga, col nome che usiamo noi: il canone si chiama "Villa Fago, canone ridotto" anche se
 * in rubrica il cliente è "GVF S.r.l.", quindi vince la parte prima della virgola; senza,
 * il cliente in rubrica o la controparte.
 */
function chiPaga(e: EntrataFissa): string {
  const i = e.name.indexOf(',');
  if (i > 0) return e.name.slice(0, i).trim();
  return e.clientName ?? e.counterparty ?? e.name;
}

/** Il canone senza il nome del cliente davanti: "AMA Properties, affitto" → "affitto". */
function cosa(e: EntrataFissa): string {
  const nome = e.name.trim();
  const i = nome.indexOf(',');
  return i > 0 ? nome.slice(i + 1).trim() : nome;
}

/** Quando entra, detto in parole: "ogni mese, il 5", "dal 10 novembre al 10 dicembre", "finito il 10 ottobre". */
function quandoEntra(e: EntrataFissa, fase: ReturnType<typeof faseEntrata>): string {
  if (fase === 'ferma') return 'non si incassa più';
  if (fase === 'finita') return e.endDate ? `finito ${ilGiorno(e.endDate)}` : 'finito';
  if (fase === 'futura') return `${dalGiorno(e.startDate)}${e.endDate ? ` ${alGiorno(e.endDate)}` : ''}`;
  const ogni = e.cycle === 'MONTHLY' ? `ogni mese${e.dayOfMonth ? `, il ${e.dayOfMonth}` : ''}` : (OGNI_QUANTO_ENTRATA[e.cycle] ?? '');
  return e.endDate ? `${ogni}, fino ${alGiorno(e.endDate)}` : ogni;
}

/** Perché un canone non ha un progetto: una voce cumulativa o l'affitto non sono lavori. */
function senzaProgetto(e: EntrataFissa): string {
  if (/aggregat/i.test(`${e.name} ${e.counterparty ?? ''}`)) return 'più clienti in una voce sola';
  if (/affitto|sublocazion|co-?working/i.test(e.name)) return "sublocazione dell'ufficio";
  return 'senza progetto';
}

function useEntrate() {
  const { data, isLoading, isError } = useQuery(entrateFisseQuery);
  const oggi = new Date().toISOString().slice(0, 10);
  const conFase = (data?.items ?? []).map((e) => ({ e, fase: faseEntrata(e, oggi) }));
  const vive = conFase.filter((x) => x.fase === 'in_corso' || x.fase === 'futura');
  const altre = conFase.filter((x) => x.fase === 'finita' || x.fase === 'ferma');
  const inCorso = conFase.filter((x) => x.fase === 'in_corso');
  return { data, isLoading, isError, vive, altre, inCorso };
}

export function EntrateFisse() {
  const navigate = useNavigate();
  const { data, isLoading, isError, vive, altre, inCorso } = useEntrate();
  const [tutte, setTutte] = useState(false);
  if (isError) return null;
  const righe = tutte ? [...vive, ...altre] : vive;

  return (
    <section className="ft-blocco" aria-label="Entrate fisse">
      <h2 className="ft-h2"><Icon name="repeat" size={15} /> Entrate fisse <span className="ft-h2-sub">canoni che fatturiamo a mano</span></h2>
      {isLoading && !data ? <p className="ft-vuoto">Carico le entrate fisse…</p> : (
        <>
          <p className="ft-frase">
            {inCorso.length > 0
              ? <>Ogni mese entrano <strong>€ {fmtEur(data!.totals.monthlyGross)}</strong> da {inCorso.length === 1 ? 'un canone' : `${inCorso.length} canoni`}, IVA compresa.</>
              : <>Oggi non è in corso nessun canone fatturato a mano.</>}
          </p>
          {righe.length > 0 && (
            <div className="ft-lista">
              {righe.map(({ e, fase }) => (
                <div key={e.id} className={`ft-entrata is-${fase}`}>
                  <BrandLogo name={chiPaga(e)} size={32} />
                  <span className="ft-chi">
                    {e.clientId
                      ? <a className="t ft-link-fattura" onClick={() => navigate(`/clients/${e.clientId}`)}>{nomeBreve(chiPaga(e))}</a>
                      : <span className="t">{nomeBreve(chiPaga(e))}</span>}
                    <span className="s">{cosa(e)}</span>
                  </span>
                  <span className="ft-progetto">
                    {e.project
                      ? <Link to={`/projects/${e.project.id}`} className="ft-link-progetto"><Icon name="cube" size={12} /> {e.project.name}</Link>
                      : <span className="mute">{senzaProgetto(e)}</span>}
                  </span>
                  <span className="ft-entrata-quando">{quandoEntra(e, fase)}</span>
                  <span className="ft-cifra">€ {fmtEur(e.amountGross)}<span className="u"> {OGNI_QUANTO_ENTRATA[e.cycle] ?? ''}</span></span>
                </div>
              ))}
            </div>
          )}
          {altre.length > 0 && (
            <button type="button" className="ft-mostra" onClick={() => setTutte((v) => !v)}>
              <Icon name={tutte ? 'eyeOff' : 'eye'} size={13} />
              {tutte ? 'Nascondi i canoni finiti o fermi' : `Mostra anche ${altre.length === 1 ? 'il canone finito o fermo' : `i ${altre.length} canoni finiti o fermi`}`}
            </button>
          )}
        </>
      )}
    </section>
  );
}

/** La stessa cosa sul telefono: un gruppo di righe, il totale nel titolo del gruppo. */
export function EntrateFisseMobile() {
  const navigate = useNavigate();
  const { data, isLoading, isError, vive, inCorso } = useEntrate();
  if (isError) return null;
  return (
    <MGroup title="Entrate fisse" count={vive.length || undefined}
      note={data && inCorso.length > 0 ? `Ogni mese entrano € ${fmtEur(data.totals.monthlyGross)} dai canoni che fatturiamo a mano, IVA compresa.` : undefined}>
      {isLoading && !data ? <MEmpty>Carico le entrate fisse…</MEmpty>
        : vive.length === 0 ? <MEmpty>Oggi non è in corso nessun canone fatturato a mano.</MEmpty>
        : vive.map(({ e, fase }) => (
          <MRow key={e.id} text={iniziali(chiPaga(e))} round title={nomeRiga(chiPaga(e))}
            sub={`${cosa(e)} · ${quandoEntra(e, fase)}`}
            trail={euroRiga(e.amountGross)}
            onClick={e.project ? () => navigate(`/projects/${e.project!.id}`) : undefined} />
        ))}
    </MGroup>
  );
}
